import { useId } from 'react';
import { isKnown } from '../../store/assessmentFacts.js';
import { money, compact } from './format.js';

// The pictures an advisor draws over, shared by the meeting and the printed
// pages so the two cannot look different.

const CHART_W = 900;
const CHART_H = 300;

// Income by where it comes from, against spending. Horizontal because the
// labels are words and the figures are long: across the page they sit beside
// their own bar and read in one line. The income bar is split by source so the
// household sees what work, Social Security, pensions and everything else each
// contribute, which is the first thing that changes at retirement.
const SOURCE_COLORS = {
  Employment: '#015697',
  'Social Security': '#3d7fb5',
  Pension: '#6fa3cf',
  'Other income': '#a9c7de',
};

const SOURCE_KEYS = { employment: 'Employment', socialSecurity: 'Social Security', pension: 'Pension', other: 'Other income' };

/**
 * `segments` names the income by source directly ([{ key, label, value }]),
 * which is how the goal sheet's period hands it over; `rows` is the older
 * path from this year's questionnaire rows, kept for saved drawings.
 */
export function CashChart({ rows, segments, income, spending, period, incomeLabel, spendingLabel }) {
  const peak = Math.max(income, spending, 1);
  const left = 210;
  const right = CHART_W - 150;
  const width = v => Math.max(v > 0 ? 2 : 0, (v / peak) * (right - left));
  let byKind = segments
    ? segments.filter(s => s.value > 0).map(s => ({ kind: SOURCE_KEYS[s.key] || 'Other income', label: s.label, value: s.value }))
    : Object.keys(SOURCE_COLORS).map(kind => ({
      kind,
      value: (rows || []).filter(r => r.kind === kind && isKnown(r.amount)).reduce((s, r) => s + r.amount.value, 0),
    })).filter(s => s.value > 0);
  // A saved basis from before rows carried their source draws one solid bar.
  if (!byKind.length && income > 0) byKind = [{ kind: 'Employment', value: income, plain: true }];

  let x = left;
  const bars = byKind.map(s => {
    const seg = { ...s, x, w: width(s.value) };
    x += seg.w;
    return seg;
  });

  return (
    <svg
      className="conv-chart"
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`Annual income ${money(income)} by source and annual spending ${money(spending)}`}
    >
      <text x={left - 18} y={96} textAnchor="end" className="conv-axis" fontSize="16">Income</text>
      {bars.map(s => (
        <rect key={s.label || s.kind} x={s.x} y={62} width={s.w} height="52" fill={SOURCE_COLORS[s.kind]} />
      ))}
      <text x={left + width(income) + 16} y={97} fontSize="24">{incomeLabel || money(income)}</text>

      <text x={left - 18} y={182} textAnchor="end" className="conv-axis" fontSize="16">Spending</text>
      <rect x={left} y={148} width={width(spending)} height="52" fill="#d9e5ee" rx="2" />
      <text x={left + width(spending) + 16} y={183} fontSize="24">{spendingLabel || money(spending)}</text>

      <line x1={left} y1="44" x2={left} y2={CHART_H - 60} stroke="#d9e5ee" strokeWidth="1.5" />
      {bars.filter(s => !s.plain).map((s, i) => (
        <g key={`legend-${s.label || s.kind}`} transform={`translate(${left + i * 200}, ${CHART_H - 28})`}>
          <rect width="14" height="14" y="-11" fill={SOURCE_COLORS[s.kind]} />
          <text x="20" className="conv-axis" fontSize="13">{`${s.label || s.kind} ${compact(s.value)}`}</text>
        </g>
      ))}
      <text x={left} y={CHART_H - 4} className="conv-axis">{`Annual dollars · ${period}`}</text>
    </svg>
  );
}

// The household's cash flow, year by year: what comes in, by where it comes
// from, against what they spend. This is the story an advisor tells in this
// part of the meeting (the salary stops, the years before Social Security are
// lived on savings, then the benefits come on), drawn for this household so
// the advisor's marker can go straight to the part that matters.
//
// The stretches of the story are marked across the top, each with what savings
// supply in it: the working years, the first years of retirement before every
// benefit has started, and the years once every benefit has. They used to be a
// choice beside the slide; now they are on the picture (Jimmy, 2026-10-01).
//
// It draws only what is recorded. The space between income and spending is
// shaded as coming from savings only in retired years with every figure known;
// where Social Security or a salary was never entered, that part is hatched
// and named instead of being drawn as if it were nothing.
const TL_W = 1000;
const TL_H = 386;
const TL = { left: 58, right: 990, top: 96, bottom: 338 };
// Two rows of words for the stretches, and one line for all their brackets,
// so every bracket sits at the same height whichever row its words are on.
const TL_PERIOD_ROWS = [2, 40];
const TL_BRACKET_Y = 84;
export const TL_SOURCES = [
  ['pension', 'Pension', '#6fa3cf'],
  ['socialSecurity', 'Social Security', '#3d7fb5'],
  ['other', 'Other income', '#a9c7de'],
  ['employment', 'Employment', '#015697'],
];
export const TL_WASH = '#f6d9d3';
const TL_PERIOD = { working: '#5f6f7d', bridge: '#b4532a', steady: '#015697', retired: '#5f6f7d' };

function niceStep(peak) {
  const steps = [5000, 10000, 20000, 25000, 50000, 100000, 200000, 250000, 500000, 1000000, 2000000, 5000000];
  return steps.find(s => peak / s <= 5) || steps[steps.length - 1];
}

export function CashTimeline({ model }) {
  const hatch = useId().replace(/:/g, '');
  if (!model || !Array.isArray(model.rows) || model.rows.length < 2) return null;
  const { rows, state, salaryMissing, periods = [], milestones = [] } = model;
  const n = rows.length;
  const band = (TL.right - TL.left) / n;
  const barW = Math.max(3, band * 0.74);
  const stackOf = r => TL_SOURCES.map(([key, label, fill]) => ({ key, label, fill, value: Math.max(0, r[key] || 0) }));
  const topOf = r => stackOf(r).reduce((s, x) => s + x.value, 0);
  // A working year with no salary recorded has no income to draw.
  const blankYear = r => salaryMissing && !r.retired;

  const peak = Math.max(1, ...rows.map(r => Math.max(r.spending || 0, blankYear(r) ? 0 : topOf(r))));
  const step = niceStep(peak);
  const max = Math.ceil((peak * 1.04) / step) * step;
  const y = v => TL.bottom - (Math.min(v, max) / max) * (TL.bottom - TL.top);
  const x = i => TL.left + i * band;
  const indexOf = year => rows.findIndex(r => r.year === year);

  const ticks = [];
  for (let v = 0; v <= max; v += step) ticks.push(v);

  // The stretches across the top. A stretch's words go in whichever row lets
  // them start nearest its own bracket without running into the words before
  // them, so stretches a year or two long never print over each other; the
  // bracket itself always spans exactly its own years, in the same colour.
  const rowEnds = TL_PERIOD_ROWS.map(() => -Infinity);
  const spans = periods.map(p => {
    const a = Math.max(0, indexOf(p.from));
    const bIdx = indexOf(p.to);
    const b = bIdx < 0 ? n - 1 : bIdx;
    const x0 = x(a);
    const x1 = x(b + 1);
    const width = Math.max(p.label.length * 8.2, p.note.length * 7.4) + 8;
    let row = 0;
    let from = Infinity;
    rowEnds.forEach((end, r) => {
      const at = Math.max(x0, end + 14);
      if (at < from) { from = at; row = r; }
    });
    from = Math.min(from, TL.right - width);
    rowEnds[row] = from + width;
    return { ...p, x0, x1, ty: TL_PERIOD_ROWS[row], flip: false, tx: from + 2 };
  });
  const boundaries = spans.slice(1).map(s => s.x0);

  // Moments inside a stretch, written just inside the top of the plot, on
  // whichever side of their line does not run into a stretch's dashed line.
  const crosses = (a, b) => boundaries.some(bx => bx > a - 3 && bx < b + 3);
  let lastEnd = -Infinity;
  const moments = milestones.map(m => ({ ...m, i: indexOf(m.year) })).filter(m => m.i >= 0).map(m => {
    const mx = x(m.i);
    const width = m.label.length * 6.6;
    const rightFits = mx + width + 6 <= TL.right && !crosses(mx + 4, mx + 4 + width);
    const leftFits = mx - width - 4 >= TL.left && !crosses(mx - 4 - width, mx - 4);
    const flip = !rightFits && leftFits;
    const from = flip ? mx - width - 4 : mx + 4;
    const hide = from < lastEnd + 8;
    if (!hide) lastEnd = from + width;
    return { ...m, mx, flip, hide };
  });
  const MOMENT_Y = TL.top + 14;

  // The retired years where the gap is not known.
  const open = state === 'socialSecurityMissing' ? rows.map((r, i) => (r.retired && r.spending > topOf(r) ? i : -1)).filter(i => i >= 0) : [];
  const blank = rows.map((r, i) => (blankYear(r) ? i : -1)).filter(i => i >= 0);

  const spendPath = rows.map((r, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(r.spending).toFixed(1)} H${x(i + 1).toFixed(1)}`).join(' ');

  // Year labels: every fifth, the first and last, and each stretch's start,
  // never crowded.
  const wanted = [0, ...spans.map(s => indexOf(s.from)), ...moments.map(m => m.i), n - 1,
    ...rows.map((r, i) => (r.year % 5 === 0 ? i : -1))].filter(i => i >= 0);
  const labelled = [];
  wanted.forEach(i => {
    const cx = x(i) + band / 2;
    if (labelled.every(j => Math.abs(x(j) + band / 2 - cx) >= 46)) labelled.push(i);
  });
  const ages = r => (r.c1Age === null || r.c1Age === undefined ? '' : r.c2Age === null || r.c2Age === undefined ? `${r.c1Age}` : `${r.c1Age} / ${r.c2Age}`);

  return (
    <svg
      className="conv-timeline"
      viewBox={`0 0 ${TL_W} ${TL_H}`}
      style={{ aspectRatio: `${TL_W} / ${TL_H}` }}
      role="img"
      aria-label={`Income by source against spending, each year from ${rows[0].year} to ${rows[n - 1].year}. ${spans.map(s => `${s.label}, ${s.from}${s.to > s.from ? ` to ${s.to}` : ''}: ${s.note}.`).join(' ')}`}
    >
      <defs>
        <pattern id={hatch} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="8" stroke="#9fb2c2" strokeWidth="1.5" />
        </pattern>
      </defs>

      {ticks.map(v => (
        <g key={v}>
          <line x1={TL.left} x2={TL.right} y1={y(v)} y2={y(v)} stroke="#e3ebf1" strokeWidth="1" />
          <text x={TL.left - 8} y={y(v) + 4} textAnchor="end" fontSize="12" className="tl-muted">{compact(v)}</text>
        </g>
      ))}

      {/* What savings cover: retired years, every figure known. */}
      {state === 'ready' && rows.map((r, i) => (r.retired && r.spending > topOf(r)
        ? <rect key={`w${r.year}`} x={x(i)} width={band + 0.5} y={y(r.spending)} height={y(topOf(r)) - y(r.spending)} fill={TL_WASH} />
        : null))}
      {open.map(i => (
        <rect key={`o${i}`} x={x(i)} width={band + 0.5} y={y(rows[i].spending)} height={y(topOf(rows[i])) - y(rows[i].spending)} fill={`url(#${hatch})`} />
      ))}
      {blank.map(i => (
        <rect key={`b${i}`} x={x(i)} width={band + 0.5} y={y(rows[i].spending)} height={TL.bottom - y(rows[i].spending)} fill={`url(#${hatch})`} />
      ))}

      {rows.map((r, i) => {
        if (blankYear(r)) return null;
        let base = 0;
        return (
          <g key={r.year}>
            {stackOf(r).filter(s => s.value > 0).map(s => {
              const top = base + s.value;
              const el = <rect key={s.key} x={x(i) + (band - barW) / 2} width={barW} y={y(top)} height={Math.max(0, y(base) - y(top))} fill={s.fill} />;
              base = top;
              return el;
            })}
          </g>
        );
      })}

      <path d={spendPath} fill="none" stroke="#16354d" strokeWidth="2.2" strokeLinejoin="round" />
      <line x1={TL.left} x2={TL.right} y1={TL.bottom} y2={TL.bottom} stroke="#b8cfdf" strokeWidth="1" />

      {open.length ? (
        <text x={Math.min(TL.right - 220, x(open[0]) + 8)} y={y(rows[open[0]].spending) + 18} fontSize="13" className="tl-note">
          Social Security not yet recorded
        </text>
      ) : null}
      {blank.length > 2 ? (
        <text x={x(blank[0]) + 8} y={y(rows[blank[0]].spending) + 18} fontSize="13" className="tl-note">Salary not recorded</text>
      ) : null}

      {/* Where one stretch hands over to the next. */}
      {boundaries.map(bx => (
        <line key={`d${bx}`} x1={bx} x2={bx} y1={TL_BRACKET_Y} y2={TL.bottom} stroke="#7a8a99" strokeWidth="1" strokeDasharray="4 4" />
      ))}

      {/* The stretches: each bracket over its own years, all on one line, with
          its words above in the same colour. */}
      {spans.map(s => (
        <g key={s.key} className="tl-period" data-period={s.key}>
          <text x={s.tx} y={s.ty + 14} textAnchor={s.flip ? 'end' : 'start'} fontSize="14.5" fontWeight="600" style={{ fill: TL_PERIOD[s.key] }}>{s.label}</text>
          <text x={s.tx} y={s.ty + 31} textAnchor={s.flip ? 'end' : 'start'} fontSize="13" className="tl-ink">{s.note}</text>
          <path
            d={`M${s.x0 + 2} ${TL_BRACKET_Y + 6} V${TL_BRACKET_Y} H${s.x1 - 2} V${TL_BRACKET_Y + 6}`}
            fill="none"
            stroke={TL_PERIOD[s.key]}
            strokeWidth="2"
          />
        </g>
      ))}

      {moments.map(m => (m.hide ? null : (
        <g key={`${m.year}-${m.label}`}>
          <line x1={m.mx} x2={m.mx} y1={TL.top} y2={TL.bottom} stroke="#9aa8b4" strokeWidth="1" strokeDasharray="2 3" />
          <text
            x={m.flip ? m.mx - 4 : m.mx + 4}
            y={MOMENT_Y}
            textAnchor={m.flip ? 'end' : 'start'}
            fontSize="12"
            className="tl-muted"
            stroke="#fff"
            strokeWidth="3"
            style={{ paintOrder: 'stroke' }}
          >
            {m.label}
          </text>
        </g>
      )))}

      <text x="0" y={TL.bottom + 22} fontSize="11" className="tl-muted">Year</text>
      <text x="0" y={TL.bottom + 38} fontSize="11" className="tl-muted">Age</text>
      {labelled.map(i => (
        <g key={`l${i}`}>
          <text x={x(i) + band / 2} y={TL.bottom + 22} textAnchor="middle" fontSize="12.5">{rows[i].year}</text>
          <text x={x(i) + band / 2} y={TL.bottom + 38} textAnchor="middle" fontSize="11" className="tl-muted">{ages(rows[i])}</text>
        </g>
      ))}
    </svg>
  );
}

/**
 * The triangle advisors already draw on the whiteboard, with the shares that
 * used to be worked out by hand written in. `corners` is top, left, right.
 */
export function TaxTriangle({ corners, total, renderValue }) {
  const pos = ['is-top', 'is-left', 'is-right'];
  return (
    <div className="conv-tax-wrap">
      <svg className="conv-tax-lines" viewBox="0 0 560 325" preserveAspectRatio="none" aria-hidden="true">
        <path d="M280 50 L82 260 L478 260 Z" fill="none" stroke="currentColor" strokeWidth="1.5" />
      </svg>
      {corners.map((c, i) => {
        const share = c.value !== null && total > 0 ? `${Math.round((c.value / total) * 100)}%` : '';
        return (
          <div className={`conv-pool ${pos[i]}`} key={c.key}>
            <span>{c.label}{share ? ` · ${share}` : ''}</span>
            <strong className={c.value === null ? 'is-unknown' : ''}>
              {renderValue ? renderValue(c) : compact(c.value)}
            </strong>
            {c.sub ? <span>{c.sub}</span> : null}
          </div>
        );
      })}
      <div className="conv-tax-center">
        {total > 0 ? (
          <>
            <strong>{compact(total)}</strong>
            <br />
            in investment accounts
          </>
        ) : (
          <>
            Account types shape
            <br />
            planning questions.
          </>
        )}
      </div>
    </div>
  );
}

// A split bar under the triangle, because a triangle does not show proportion
// and the proportion is the point.
export function SplitBar({ parts }) {
  const total = parts.reduce((s, p) => s + (p.value || 0), 0);
  if (total <= 0) return null;
  return (
    <div className="conv-split" role="img" aria-label={parts.map(p => `${p.label} ${Math.round(((p.value || 0) / total) * 100)}%`).join(', ')}>
      {parts.filter(p => p.value > 0).map(p => (
        <span key={p.key} style={{ flex: p.value, background: p.fill }} title={`${p.label} ${money(p.value)}`} />
      ))}
    </div>
  );
}

// The allocation pie advisors know from the Morningstar page, with the
// stock-to-bond ratio in the middle because that is the figure Jake reads
// before any other (2026-09-29).
export function Donut({ parts, centre, sub }) {
  const total = parts.reduce((s, p) => s + p.value, 0);
  if (total <= 0) return null;
  const R = 70;
  const C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <div className="conv-donut">
      <svg viewBox="0 0 200 200" role="img" aria-label={parts.map(p => `${p.label} ${Math.round(p.pct)}%`).join(', ')}>
        {parts.map(p => {
          const len = (p.value / total) * C;
          const el = (
            <circle
              key={p.key}
              r={R} cx="100" cy="100"
              fill="none"
              stroke={p.fill}
              strokeWidth="30"
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 100 100)"
            />
          );
          offset += len;
          return el;
        })}
        {centre ? <text x="100" y="98" textAnchor="middle" fontSize="26" fontFamily="Georgia, serif">{centre}</text> : null}
        {sub ? <text x="100" y="120" textAnchor="middle" fontSize="11" fill="#52697c">{sub}</text> : null}
      </svg>
      <ul className="conv-donut-legend">
        {parts.map(p => (
          <li key={p.key}>
            <span className="conv-swatch-dot" style={{ background: p.fill }} />
            <span>{p.label}</span>
            <strong>{`${Math.round(p.pct)}%`}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}
